/**
 * Search Ranking Utilities — V107.52.34
 *
 * Pure scoring and query-shape detection helpers used by the global search
 * (`useGlobalSearch`). Hand-rolled to keep things lean — no Fuse.js dep.
 *
 * Scoring strategy (per field):
 *   exact       → 10
 *   startsWith  → 5
 *   word-start  → 3
 *   substring   → 1
 *   no match    → 0
 *
 * The search hook layers entity-type "activity boosts" on top (open faults
 * +5, active deals +3, etc.) and a smart-query field boost (digits-only
 * query → phone/serial fields get +5, contains "@" → email +5, etc.).
 */

export {
  normaliseName,
  normalisePhone,
  normaliseEmail,
} from '@/services/customer-merge.service';

// ---------------------------------------------------------------------------
// Query type detection
// ---------------------------------------------------------------------------

export interface QueryShape {
  /** Original raw query, untrimmed */
  raw: string;
  /** Lowercased, trimmed */
  normalized: string;
  /** Digits-only version (for phone matching) */
  digitsOnly: string;
  /** True when the user typed only digits/separators and ≥6 digits */
  isPhone: boolean;
  /** True when query contains "@" */
  isEmail: boolean;
  /** True when query matches TF-YYYY-NNNN account-number shape */
  isAccountNumber: boolean;
  /** True when query starts with "#" — interpreted as ID lookup */
  isIdLookup: boolean;
  /**
   * True when query LOOKS like a serial number — alphanumeric (with dashes),
   * ≥4 chars, AND is NOT a pure phone number. Triggers the on-demand
   * `searchSerialNumbers()` Firestore call (debounced).
   */
  isSerialShaped: boolean;
}

const ACCOUNT_NUMBER_RE = /^[A-Z]{2,4}-\d{4}-\d{2,6}$/i;
const SERIAL_SHAPE_RE = /^[A-Z0-9][A-Z0-9-]{2,}[A-Z0-9]$/i;

export function detectQueryShape(raw: string): QueryShape {
  const normalized = raw.trim().toLowerCase();
  const digitsOnly = raw.replace(/\D/g, '');
  const isPhone = digitsOnly.length >= 6 && /^[\d\s()+\-.]+$/.test(raw.trim());
  const isEmail = normalized.includes('@');
  const isAccountNumber = ACCOUNT_NUMBER_RE.test(raw.trim());
  const isIdLookup = raw.trim().startsWith('#');
  // A serial is alphanumeric-with-dashes, ≥4 chars. Alphanumeric serials
  // (containing a letter) qualify at 4+ chars; PURE-NUMERIC serials / kit
  // numbers / IMEIs qualify at 6+ digits. We deliberately do NOT exclude
  // phone-shaped queries here: a contiguous ≥6-digit string could be either a
  // phone OR a serial, so we let it trigger the (in-memory-cheap, Firestore-
  // debounced) serial lookup as well — otherwise numeric serials are
  // completely unfindable in global search.
  const isSerialShaped =
    !isEmail &&
    SERIAL_SHAPE_RE.test(raw.trim()) &&
    (/[A-Za-z]/.test(raw) || digitsOnly.length >= 6);

  return {
    raw,
    normalized,
    digitsOnly,
    isPhone,
    isEmail,
    isAccountNumber,
    isIdLookup,
    isSerialShaped,
  };
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/**
 * Score how well a single field matches the query.
 * Returns 0 when no match, otherwise the highest-tier match found.
 */
export function scoreFieldMatch(query: string, fieldText: string | undefined | null): number {
  if (!query || !fieldText) return 0;
  const q = query.trim().toLowerCase();
  const f = String(fieldText).toLowerCase();
  if (!f) return 0;

  if (f === q) return 10;
  if (f.startsWith(q)) return 5;
  // Word-boundary match — check if any token in f starts with q. Split on
  // whitespace AND common separators (hyphen / slash / comma / dot) so
  // hyphenated names ("Smith-Jones") and slashed addresses ("12/34 King St")
  // still earn the word-start tier instead of collapsing to a bare substring.
  const words = f.split(/[\s\-/,.]+/);
  for (const w of words) {
    if (w && w.startsWith(q)) return 3;
  }
  if (f.includes(q)) return 1;
  return 0;
}

/**
 * Canonicalise an AU phone number's digits to its national subscriber form so
 * the same number stored in different formats compares equal:
 *   +61 412 345 678  → "61412345678" → "412345678"
 *   0412 345 678     → "0412345678"  → "412345678"
 *   (02) 4883 6781   → "0248836781"  → "248836781"
 * The length guards stop us mangling short tail-fragments a user types to
 * recall a number (e.g. "3456" stays "3456", "0123" stays "0123").
 */
export function canonicalAuDigits(digits: string): string {
  let d = digits;
  // Drop the +61 country code when it precedes a full 9/10-digit national number.
  if (d.startsWith('61') && d.length >= 10) d = d.slice(2);
  // Drop the national trunk '0' when it precedes a full national number.
  if (d.startsWith('0') && d.length >= 9) d = d.slice(1);
  return d;
}

/**
 * Score a phone-shaped match against a phone field (digits-only on both sides).
 * Returns 8 (between exact and startsWith) when matched, 0 otherwise.
 *
 * Compares BOTH the raw digit strings and their AU-canonical forms, so a number
 * stored as "+61 412 345 678" matches a locally-typed "0412 345 678" (and vice
 * versa) — this is common for imported / externally-synced records.
 */
export function scorePhoneMatch(queryDigits: string, phoneRaw: string | undefined | null): number {
  if (!queryDigits || !phoneRaw) return 0;
  const phoneDigits = String(phoneRaw).replace(/\D/g, '');
  if (phoneDigits.length < 6) return 0;

  const cPhone = canonicalAuDigits(phoneDigits);
  const cQuery = canonicalAuDigits(queryDigits);

  // Exact — raw or canonical.
  if (phoneDigits === queryDigits || (cQuery && cPhone === cQuery)) return 10;
  // Tail-match: people often type the last 4-6 digits to recall a customer.
  if (
    cQuery.length >= 4 &&
    (phoneDigits.endsWith(queryDigits) || cPhone.endsWith(cQuery))
  ) {
    return 8;
  }
  if (phoneDigits.includes(queryDigits) || (cQuery.length >= 4 && cPhone.includes(cQuery))) {
    return 5;
  }
  return 0;
}

/**
 * Score the best match across multiple fields. Returns the max score, or 0
 * if no field matched. Useful for "did this entity match at all?" gates.
 */
export function bestFieldScore(query: string, fields: Array<string | undefined | null>): number {
  let max = 0;
  for (const f of fields) {
    const s = scoreFieldMatch(query, f);
    if (s > max) max = s;
  }
  return max;
}

/**
 * Sum the scores across multiple fields. Used when an entity matches in
 * multiple places — e.g. customer name AND address — to rank it higher
 * than one that only matched in one field.
 */
export function sumFieldScores(query: string, fields: Array<string | undefined | null>): number {
  let total = 0;
  for (const f of fields) total += scoreFieldMatch(query, f);
  return total;
}
